import { useCallback } from 'react';
import { useMount } from 'react-use';

import {
  type AnonymousAccountTrackingLocation,
  useTrackCreatedAnonymousAccount,
  useTrackLoggedInToAnonymousAccount,
  useTrackSignedUp,
  useTrackViewedAnonymousAccountCreationForm,
} from '@air/analytics';
import { type ShortIdObjectType } from '@air/provider-short-id';
import { useToasts } from '@air/provider-toast';

import { useAnonymousSignUp } from '~/components/PublicLinkAnonymousSignUpModal/hooks/useAnonymousSignUp';
import { useGetRecaptchaToken } from '~/hooks/useGetRecaptchaToken';

export type UseAnonymousSignUpFormArgs = {
  shortId: string;
  objectId: string;
  objectType: ShortIdObjectType;
  trackLocation: AnonymousAccountTrackingLocation;
  onLoggedIn: () => void;
};

export const useAnonymousSignUpForm = ({
  shortId,
  objectId,
  objectType,
  trackLocation,
  onLoggedIn,
}: UseAnonymousSignUpFormArgs) => {
  const { showToast } = useToasts();
  const { getRecaptchaToken } = useGetRecaptchaToken();
  const { loginWithEmail, signupWithEmailAndName } = useAnonymousSignUp();
  const { trackLoggedInToAnonymousAccount } = useTrackLoggedInToAnonymousAccount();
  const { trackCreatedAnonymousAccount } = useTrackCreatedAnonymousAccount();
  const { trackViewedAnonymousAccountCreationForm } = useTrackViewedAnonymousAccountCreationForm();
  const { trackSignedUp } = useTrackSignedUp();

  useMount(() => {
    trackViewedAnonymousAccountCreationForm({
      share_url: shortId,
      object_id: objectId,
      object_type: objectType,
      location: trackLocation,
    });
  });

  const onLoginWithEmail = useCallback(
    async (email: string) => {
      try {
        const recaptchaToken = await getRecaptchaToken();

        if (!recaptchaToken) {
          throw new Error('Can not get ReCaptcha token');
        }

        const account = await loginWithEmail({ email, recaptchaToken });

        if (account) {
          trackLoggedInToAnonymousAccount({
            share_url: shortId,
            object_id: objectId,
            object_type: objectType,
            location: trackLocation,
          });
          onLoggedIn();
          return account;
        }
      } catch (error) {
        showToast('Unknown error occurred', {
          type: 'assertive',
          withCloseButton: true,
        });
        throw error;
      }
    },
    [
      getRecaptchaToken,
      loginWithEmail,
      objectId,
      objectType,
      onLoggedIn,
      shortId,
      trackLocation,
      showToast,
      trackLoggedInToAnonymousAccount,
    ],
  );

  const onSignupWithEmailAndName = useCallback(
    async (email: string, name: string) => {
      try {
        const recaptchaToken = await getRecaptchaToken();

        if (!recaptchaToken) {
          throw new Error('Can not get ReCaptcha token');
        }

        const account = await signupWithEmailAndName({ email, name, recaptchaToken });

        trackCreatedAnonymousAccount({
          share_url: shortId,
          object_id: objectId,
          object_type: objectType,
          location: trackLocation,
        });

        trackSignedUp({
          account_type: 'anonymous',
          auth_method: 'email',
          context: 'share_link',
          id: account.sessionId,
          email,
        });

        onLoggedIn();
        return account;
      } catch {
        showToast('Unknown error occurred', {
          type: 'assertive',
          withCloseButton: true,
        });
      }
    },
    [
      getRecaptchaToken,
      signupWithEmailAndName,
      objectId,
      objectType,
      onLoggedIn,
      shortId,
      trackLocation,
      showToast,
      trackCreatedAnonymousAccount,
      trackSignedUp,
    ],
  );

  return {
    onLoginWithEmail,
    onSignupWithEmailAndName,
  };
};
